import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonButton,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonGrid,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonProgressBar,
  IonSkeletonText,
} from '@ionic/react';
import {
  closeOutline, chevronDownOutline, refreshOutline,
  trendingUp, trendingDown, calendarOutline, ribbonOutline, barChartOutline,
  cashOutline, peopleOutline, checkmarkCircleOutline, closeCircleOutline, timeOutline,
} from 'ionicons/icons';
import styles from './BookingsAnalyticsModal.module.scss';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import { PortalPickerDropdown } from './PortalPickerDropdown';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import {
  AnalyticsRange,
  ANALYTICS_RANGES,
  BookingsAnalytics,
  EMPTY_ANALYTICS,
  computeBookingsAnalytics,
  withAttendance,
  trendPct,
  fmtMoney,
} from '../utils/bookingsAnalytics';
import { useTranslation } from 'react-i18next';

interface BookingsAnalyticsModalProps {
  onClose: () => void;
}

const RANGE_OPTS = ANALYTICS_RANGES.filter(r => r.key !== 'custom');

// Compact confirmed-vs-cancelled line chart over the range's buckets.
const TrendChart: React.FC<{ analytics: BookingsAnalytics }> = ({ analytics }) => {
  const { t: tr } = useTranslation();
  const W = 320, H = 116, PL = 24, PR = 8, PT = 10, PB = 20;
  const CW = W - PL - PR;
  const CH = H - PT - PB;
  const nb = analytics.buckets.length;
  const maxBar = Math.max(
    ...analytics.buckets.map(b => b.confirmed),
    ...analytics.buckets.map(b => b.cancelled),
    1,
  );
  const hasCanc = analytics.buckets.some(b => b.cancelled > 0);
  const pts = analytics.buckets.map((b, i) => ({
    x: nb <= 1 ? PL + CW / 2 : PL + (i / (nb - 1)) * CW,
    yConf: PT + CH - (b.confirmed / maxBar) * CH,
    yCanc: PT + CH - (b.cancelled / maxBar) * CH,
    label: b.label,
    cancelled: b.cancelled,
  }));
  const line = (key: 'yConf' | 'yCanc') =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${(p as unknown as Record<string, number>)[key].toFixed(1)}`).join(' ');
  const area = () => {
    const bot = PT + CH;
    return `${line('yConf')} L${pts[pts.length - 1].x.toFixed(1)},${bot} L${pts[0].x.toFixed(1)},${bot} Z`;
  };

  if (nb < 2) return <IonText className={styles.emptyText}>{tr('components.BookingsAnalyticsModal.notEnoughDataToShowA')}</IonText>;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className={styles.chartSvg}>
      <defs>
        <linearGradient id="baGradConf" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(var(--ion-color-success-rgb), 0.28)" />
          <stop offset="100%" stopColor="rgba(var(--ion-color-success-rgb), 0.02)" />
        </linearGradient>
      </defs>
      {[0, 50, 100].map(pct => {
        const y = PT + (1 - pct / 100) * CH;
        return (
          <g key={pct}>
            <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="var(--glass-border)" strokeWidth="1" />
            <text x={PL - 4} y={y + 3} textAnchor="end" fontSize="8" fill="var(--ion-color-medium)">
              {Math.round((pct / 100) * maxBar)}
            </text>
          </g>
        );
      })}
      <path d={area()} fill="url(#baGradConf)" />
      <path d={line('yConf')} fill="none" stroke="var(--ion-color-success)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {hasCanc && (
        <path d={line('yCanc')} fill="none" stroke="var(--ion-color-danger)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.8" />
      )}
      {pts.map((p, i) => (
        <g key={i}>
          {(i === 0 || i === nb - 1 || nb <= 6) && (
            <text x={p.x} y={H - 5} textAnchor="middle" fontSize="8" fill="var(--ion-color-medium)">{p.label}</text>
          )}
          <circle cx={p.x} cy={p.yConf} r={3} fill="var(--ion-color-success)" />
          {p.cancelled > 0 && <circle cx={p.x} cy={p.yCanc} r={2.5} fill="var(--ion-color-danger)" />}
        </g>
      ))}
    </svg>
  );
};

const BookingsAnalyticsModal: React.FC<BookingsAnalyticsModalProps> = ({ onClose }) => {
  const { t: tr } = useTranslation();
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const gridRef = useRef<HTMLIonGridElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const rangeTriggerRef = useRef<HTMLButtonElement>(null);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - progress * 0.2})`);
    }
  }, []);
  const handleScroll = useScrollSnap(gridRef, 66, applyProgress);

  const [bookings, setBookings] = useState<BookingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<AnalyticsRange>('30d');
  const [rangeOpen, setRangeOpen] = useState(false);

  const fetchBookings = useCallback(async () => {
    // Also called by the refresh button, so flip loading back on (a no-op on the first mount call,
    // where it already starts true) — that is what shows the skeleton during a refresh.
    setLoading(true);
    try {
      const svc = DataServiceFactory.getService();
      if (!svc) return;
      const now = new Date();
      const from = new Date(); from.setFullYear(now.getFullYear() - 1);
      const data = await svc.bookings.getBookingsAndBlockedSlotsByDateRange(from, now, []);
      if (Array.isArray(data)) {
        const real = data.filter((b: BookingItem) => b.type !== 'blocked');
        setBookings(await withAttendance(real, ids => svc.bookings.getBookingsAttendance(ids)));
      }
    } catch (err) {
      console.error('[BookingsAnalyticsModal] Failed to load bookings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body).
    Promise.resolve().then(() => fetchBookings());
  }, [fetchBookings]);

  const analytics: BookingsAnalytics = useMemo(
    () => (bookings.length ? computeBookingsAnalytics(bookings, range) : EMPTY_ANALYTICS),
    [bookings, range],
  );

  const totalTrend = trendPct(analytics.total, analytics.prevTotal);
  const revTrend = trendPct(analytics.revenue, analytics.prevRevenue);
  const rangeLabel = RANGE_OPTS.find(r => r.key === range)?.label ?? '';

  const trendNode = (t: { pct: number; up: boolean } | null) => t && (
    <span className={`${styles.trend} ${t.up ? styles.trendUp : styles.trendDown}`}>
      <IonIcon icon={t.up ? trendingUp : trendingDown} />{tr('components.BookingsAnalyticsModal.vsPrev', { pct: t.pct })}</span>
  );

  const statCard = (
    icon: string, iconClass: string, label: string, value: React.ReactNode, sub?: React.ReactNode,
  ) => (
    <IonCard className={styles.metricCard}>
      <IonCardContent className={styles.metricContent}>
        <div className={`${styles.metricIconCircle} ${iconClass}`}>
          <IonIcon icon={icon} className={styles.metricIcon} />
        </div>
        <IonText className={styles.metricValue}>{value}</IonText>
        <IonText className={styles.metricLabel}>{label}</IonText>
        {sub && <IonText className={styles.metricSub}>{sub}</IonText>}
      </IonCardContent>
    </IonCard>
  );

  return (
    <div className={styles.modalWrapper}>
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={tr('components.BookingsAnalyticsModal.analytics')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
          <IonIcon icon={closeOutline} />
        </IonButton>
      }
      endButtons={
        <IonButton shape="round" className={styles.refreshButton} onClick={fetchBookings} disabled={loading}>
          <IonIcon icon={refreshOutline} />
        </IonButton>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
        <IonGrid
          ref={gridRef}
          className={styles.gridContainer}
          onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
        >
          <TitleCard
            ref={titleCardAnimationRef}
            title={tr('components.BookingsAnalyticsModal.bookingAnalytics')}
            cardClassName={styles.analyticsTitleCard}
            titleContainerClassName={styles.titleContainer}
            titleClassName={styles.analyticsTitle}
          />

          {/* Range selector — pill trigger + portal-anchored glass dropdown */}
          <IonGrid className={styles.rangeRow}>
            <button
              ref={rangeTriggerRef}
              className={`${styles.rangeTrigger} ${rangeOpen ? styles.rangeTriggerOpen : ''}`}
              onClick={() => setRangeOpen(v => !v)}
            >
              {rangeLabel}
              <IonIcon icon={chevronDownOutline} className={styles.rangeCaret} />
            </button>
          </IonGrid>
          <PortalPickerDropdown
            isOpen={rangeOpen}
            fieldRef={rangeTriggerRef as React.RefObject<HTMLElement | null>}
            portalRef={pickerPortalRef}
            close={() => setRangeOpen(false)}
            align="left"
            className={styles.dropdown}
          >
            <IonList lines="none" className={styles.dropdownList}>
              {RANGE_OPTS.map(r => {
                const active = r.key === range;
                return (
                  <IonItem
                    key={r.key}
                    button
                    detail={false}
                    lines="none"
                    className={`${styles.dropdownItem} ${active ? styles.dropdownItemActive : ''}`}
                    onClick={() => { setRange(r.key); setRangeOpen(false); }}
                  >
                    <IonLabel color={active ? 'primary' : undefined}>{r.label}</IonLabel>
                  </IonItem>
                );
              })}
            </IonList>
          </PortalPickerDropdown>

          {loading ? (
            <div aria-hidden="true">
              {/* Loading placeholders: fixed text (card labels, section titles, legend, table headers) and
                  icons are real; only data (values, names, chart, table cells) is a bar. */}
              <IonGrid className={styles.metricsGrid}>
                {([
                  [barChartOutline, styles.iconBlue, 'Total Bookings'],
                  [cashOutline, styles.iconGreen, 'Revenue'],
                  [checkmarkCircleOutline, styles.iconPurple, 'Confirmed'],
                  [timeOutline, styles.iconAmber, 'Pending'],
                  [closeCircleOutline, styles.iconRed, 'Cancelled'],
                  [peopleOutline, styles.iconBlue, 'Unique Clients'],
                ] as [string, string, string][]).map(([icon, iconClass, label]) => (
                  <IonCard key={label} className={`${styles.metricCard} ${styles.skeletonCard}`}>
                    <IonCardContent className={styles.metricContent}>
                      <div className={`${styles.metricIconCircle} ${iconClass}`}>
                        <IonIcon icon={icon} className={styles.metricIcon} />
                      </div>
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '45%', height: 22, margin: '1px auto' }} />
                      <IonText className={styles.metricLabel}>{label}</IonText>
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '70%', height: 10, margin: '1px auto' }} />
                    </IonCardContent>
                  </IonCard>
                ))}
              </IonGrid>

              <IonCard className={`${styles.sectionCard} ${styles.skeletonCard}`}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={barChartOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.bookingsOverTime')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className={styles.chartLegend}>
                    <span><span className={`${styles.legendDot} ${styles.dotGreen}`} />{tr('components.BookingsAnalyticsModal.confirmed')}</span>
                    <span><span className={`${styles.legendDot} ${styles.dotRed}`} />{tr('components.BookingsAnalyticsModal.cancelled')}</span>
                  </div>
                  <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '100%', height: 116, margin: 0, borderRadius: 8 }} />
                </IonCardContent>
              </IonCard>


              <IonCard className={`${styles.sectionCard} ${styles.skeletonCard}`}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={ribbonOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.topServices')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {[0, 1, 2].map(i => (
                    <div key={i} className={styles.serviceRow}>
                      <div className={styles.serviceMeta}>
                        <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '40%', height: 14, margin: '1px 0' }} />
                        <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '22%', height: 12, margin: '1px 0' }} />
                      </div>
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '100%', height: 5, margin: 0, borderRadius: 3 }} />
                    </div>
                  ))}
                </IonCardContent>
              </IonCard>


              <IonCard className={`${styles.sectionCard} ${styles.skeletonCard}`}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={peopleOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.topClients')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {[0, 1, 2].map(i => (
                    <div key={i} className={styles.serviceRow}>
                      <div className={styles.serviceMeta}>
                        <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '40%', height: 14, margin: '1px 0' }} />
                        <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '22%', height: 12, margin: '1px 0' }} />
                      </div>
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '100%', height: 5, margin: 0, borderRadius: 3 }} />
                    </div>
                  ))}
                </IonCardContent>
              </IonCard>


              <IonCard className={`${styles.sectionCard} ${styles.skeletonCard}`}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={calendarOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.periodBreakdown')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className={styles.tableWrap}>
                    <table className={styles.periodTable}>
                      <thead>
                        <tr>
                          <th>{tr('components.BookingsAnalyticsModal.period')}</th>
                          <th>{tr('components.BookingsAnalyticsModal.total')}</th>
                          <th>{tr('components.BookingsAnalyticsModal.done')}</th>
                          <th>{tr('components.BookingsAnalyticsModal.pend')}</th>
                          <th>{tr('components.BookingsAnalyticsModal.canc')}</th>
                          <th>{tr('components.BookingsAnalyticsModal.revenue')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[0, 1, 2, 3, 4].map(i => (
                          <tr key={i}>
                            {[0, 1, 2, 3, 4, 5].map(j => (
                              <td key={j}><IonSkeletonText animated className={styles.skeletonBar} style={{ width: j === 0 ? 56 : 24, height: 12, margin: '1px 0' }} /></td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </IonCardContent>
              </IonCard>
            </div>
          ) : (
            <>
              {/* KPI stat cards */}
              <IonGrid className={styles.metricsGrid}>
                {statCard(barChartOutline, styles.iconBlue, 'Total Bookings', analytics.total, trendNode(totalTrend))}
                {statCard(cashOutline, styles.iconGreen, 'Revenue', fmtMoney(analytics.revenue),
                  revTrend ? trendNode(revTrend) : `avg ${fmtMoney(analytics.avgValue)} / job`)}
                {statCard(checkmarkCircleOutline, styles.iconPurple, 'Confirmed', analytics.confirmed,
                  `${analytics.completionRate}% completion rate`)}
                {statCard(timeOutline, styles.iconAmber, 'Pending', analytics.pending, 'awaiting confirmation')}
                {statCard(closeCircleOutline, styles.iconRed, 'Cancelled', analytics.cancelled,
                  `${analytics.cancellationRate}% cancellation rate`)}
                {statCard(peopleOutline, styles.iconBlue, 'Unique Clients', analytics.uniqueClients,
                  `avg ${fmtMoney(analytics.avgValue)} / client`)}
              </IonGrid>

              {/* Trend chart */}
              <IonCard className={styles.sectionCard}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={barChartOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.bookingsOverTime')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className={styles.chartLegend}>
                    <span><span className={`${styles.legendDot} ${styles.dotGreen}`} />{tr('components.BookingsAnalyticsModal.confirmed')}</span>
                    <span><span className={`${styles.legendDot} ${styles.dotRed}`} />{tr('components.BookingsAnalyticsModal.cancelled')}</span>
                  </div>
                  <TrendChart analytics={analytics} />
                </IonCardContent>
              </IonCard>

              {/* Top Services */}
              <IonCard className={styles.sectionCard}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={ribbonOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.topServices')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {analytics.topServices.length === 0 ? (
                    <IonText className={styles.emptyText}>{tr('components.BookingsAnalyticsModal.noDataForThisPeriod')}</IonText>
                  ) : (
                    analytics.topServices.map((s, i) => (
                      <div key={i} className={styles.serviceRow}>
                        <div className={styles.serviceMeta}>
                          <IonText className={styles.serviceName}>{s.name}</IonText>
                          <IonText className={styles.serviceCount}>{tr('components.BookingsAnalyticsModal.bookings', { count: s.count, pct: s.pct })}</IonText>
                        </div>
                        <IonProgressBar value={s.pct / 100} className={styles.serviceProgress} />
                      </div>
                    ))
                  )}
                </IonCardContent>
              </IonCard>

              {/* Top Clients */}
              <IonCard className={styles.sectionCard}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={peopleOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.topClients')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {analytics.topClients.length === 0 ? (
                    <IonText className={styles.emptyText}>{tr('components.BookingsAnalyticsModal.noDataForThisPeriod')}</IonText>
                  ) : (
                    analytics.topClients.map((c, i) => (
                      <div key={i} className={styles.serviceRow}>
                        <div className={styles.serviceMeta}>
                          <IonText className={styles.serviceName}>{c.name}</IonText>
                          <IonText className={styles.serviceCount}>
                            {c.count} {c.count === 1 ? 'booking' : 'bookings'}
                            {c.revenue > 0 && ` · ${fmtMoney(c.revenue)}`}
                          </IonText>
                        </div>
                        <IonProgressBar
                          value={c.count / (analytics.topClients[0]?.count || 1)}
                          className={styles.serviceProgress}
                        />
                      </div>
                    ))
                  )}
                </IonCardContent>
              </IonCard>

              {/* Period breakdown — table (matches desktop) */}
              <IonCard className={styles.sectionCard}>
                <IonCardHeader>
                  <IonCardTitle className={styles.sectionTitle}>
                    <IonIcon icon={calendarOutline} className={styles.sectionIcon} />{tr('components.BookingsAnalyticsModal.periodBreakdown')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {analytics.buckets.length === 0 ? (
                    <IonText className={styles.emptyText}>{tr('components.BookingsAnalyticsModal.noDataForThisPeriod')}</IonText>
                  ) : (
                    <div className={styles.tableWrap}>
                      <table className={styles.periodTable}>
                        <thead>
                          <tr>
                            <th>{tr('components.BookingsAnalyticsModal.period')}</th>
                            <th>{tr('components.BookingsAnalyticsModal.total')}</th>
                            <th>{tr('components.BookingsAnalyticsModal.done')}</th>
                            <th>{tr('components.BookingsAnalyticsModal.pend')}</th>
                            <th>{tr('components.BookingsAnalyticsModal.canc')}</th>
                            <th>{tr('components.BookingsAnalyticsModal.revenue')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.buckets.map((b, i) => (
                            <tr key={i}>
                              <td className={styles.tdPeriod}>{b.label}</td>
                              <td><strong>{b.total}</strong></td>
                              <td className={styles.tdGreen}>{b.confirmed}</td>
                              <td className={b.pending > 0 ? styles.tdAmber : styles.tdMuted}>{b.pending}</td>
                              <td className={b.cancelled > 0 ? styles.tdRed : styles.tdMuted}>{b.cancelled}</td>
                              <td>{fmtMoney(b.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </IonCardContent>
              </IonCard>
            </>
          )}
        </IonGrid>
      </IonContent>
    </BasePageWithHeader>
    </div>
  );
};

export default BookingsAnalyticsModal;
